import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Button, SimpleLoading, Text } from "@core/index";
import { useGetVisualMatching } from "@packages/api/madlenAPI";
import LoadingContainer from "@packages/components/Loading";
import ErrorComponent from "@features/tools/components/error-component";
import { ArrowLeft, Monitor } from "lucide-react";
import VisualMatchingSmartboardView from "./visual-matching-smartboard-view";

const VisualMatchingDetailView = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSmartboardOpen, setIsSmartboardOpen] = useState(false);

  const {
    data: visualMatchingData,
    isLoading,
    error,
  } = useGetVisualMatching(Number(id), {
    query: { enabled: !!id },
  });

  if (isLoading) {
    return <SimpleLoading fastLoading={true} />;
  }

  if (error || !visualMatchingData) {
    return (
      <ErrorComponent
        title={t("visualMatching.detail.error.title")}
        description={t("visualMatching.detail.error.description")}
      />
    );
  }

  return (
    <div className="flex-1 p-4 bg-gray-50 dark:bg-dark-500 overflow-y-auto h-full">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Button
            onClick={() => navigate("/content/visual-matching")}
            size="md"
            rounded="full"
            variant="outline"
            leftIcon={<ArrowLeft size={20} />}
          >
            {t("common.back")}
          </Button>

          {/* Smart Board Button */}
          {visualMatchingData.questions &&
            visualMatchingData.questions.length > 0 && (
              <Button
                onClick={() => setIsSmartboardOpen(true)}
                size="md"
                rounded="lg"
                variant="solid"
                className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-sm hover:shadow-md transition-all duration-200"
                leftIcon={<Monitor size={18} />}
              >
                {t("visualMatching.smartboard.openSmartboard")}
              </Button>
            )}
        </div>

        {/* Header with title and description */}
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 mb-3">
          <Text variant="h1" className="text-2xl font-bold mb-2">
            {visualMatchingData.title ||
              t("visualMatching.detail.titlePlaceholder")}
          </Text>
          <Text variant="p" className="text-gray-600 dark:text-gray-300">
            {visualMatchingData.description ||
              t("visualMatching.detail.descriptionPlaceholder")}
          </Text>
          <div className="mt-4 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>
              {t("visualMatching.detail.createdAt")}:{" "}
              {new Date(visualMatchingData.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Questions in separate cards */}
        {visualMatchingData.questions &&
        visualMatchingData.questions.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {visualMatchingData.questions.map((question, qi) => (
              <div
                key={qi}
                className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6"
              >
                <div className="mb-4">
                  <Text variant="h3" className="text-lg font-semibold mb-2">
                    {question.topic ||
                      t("visualMatching.detail.questionPlaceholder", {
                        number: qi + 1,
                      })}
                  </Text>
                  <div className="w-full h-px bg-gray-200 dark:bg-dark-400"></div>
                </div>

                <div className="space-y-4">
                  {question.options?.map((option, oi) => (
                    <div
                      key={oi}
                      className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-dark-500 rounded-lg border border-gray-200 dark:border-dark-400"
                    >
                      {/* Image */}
                      <div className="flex-shrink-0">
                        {option.imageUrl ? (
                          <img
                            src={option.imageUrl}
                            alt={option.name}
                            className="w-20 h-20 object-cover rounded-lg shadow-sm"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              target.nextElementSibling?.classList.remove(
                                "hidden",
                              );
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-20 h-20 bg-gray-200 dark:bg-dark-400 rounded-lg flex items-center justify-center ${option.imageUrl ? "hidden" : ""}`}
                        >
                          <Text
                            variant="p"
                            className="text-gray-500 dark:text-gray-400 text-sm"
                          >
                            {t("visualMatching.detail.noImage")}
                          </Text>
                        </div>
                      </div>

                      {/* Name */}
                      <div className="flex-1">
                        <Text
                          variant="p"
                          className="text-lg font-medium text-gray-900 dark:text-gray-100"
                        >
                          {option.name ||
                            t("visualMatching.detail.optionPlaceholder", {
                              number: oi + 1,
                            })}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 text-center">
            <Text variant="p" className="text-gray-500 dark:text-gray-400">
              {t("visualMatching.detail.noQuestions")}
            </Text>
          </div>
        )}

        {/* Smart Board Mode Modal */}
        {visualMatchingData && (
          <VisualMatchingSmartboardView
            isOpen={isSmartboardOpen}
            onClose={() => setIsSmartboardOpen(false)}
            visualMatchingData={visualMatchingData}
          />
        )}
      </div>
    </div>
  );
};

export default VisualMatchingDetailView;
